import { getParticipantName } from "models/Participant.model";
import React from "react";
import { selectParticipantEnableGroups, selectParticipantEntityById } from "store/domain-data/participant/participant";
import { useStoreSelector } from "store/hooks";
import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";

type Props = {
  participantId: number;
  isShowAsIndividual?: boolean;
};

const ParticipantCardDisplayName: React.FC<Props> = ({ participantId, isShowAsIndividual = false, ...otherProps }) => {
  const participant = useStoreSelector((state) => selectParticipantEntityById(state, participantId));

  const isParticipantGroupEnabled = useStoreSelector((state) =>
    selectParticipantEnableGroups(state, participant?.id || 0)
  );
  const numberOfGroupNames = React.useMemo(() => {
    if (participant?.groupList) {
      return participant.groupList.split(", ").length;
    }

    return 0;
  }, [participant]);

  if (!participant) {
    return null;
  }

  const participantName = getParticipantName(participant);
  const hasNoParticipantGroup = !participant.groupName && numberOfGroupNames === 0;
  const shouldShowAsIndividual = !isParticipantGroupEnabled || isShowAsIndividual;

  if (hasNoParticipantGroup || shouldShowAsIndividual) {
    // Show as individual or current participant does not have group enabled
    const participantOrganisation = participant.organisation;
    if (participantOrganisation) {
      return (
        <span {...otherProps}>
          {participantOrganisation} - <StyledDisplayName>{participantName}</StyledDisplayName>
        </span>
      );
    }

    return <span {...otherProps}>{participantName}</span>;
  }

  // Count the total number of names based on the group names plus current participant
  const numberOfNames = numberOfGroupNames + 1;
  const groupName = participant.groupName || "Owners Group";
  const memberLabel = numberOfNames === 1 ? "member" : "members";

  return (
    <span {...otherProps}>
      {groupName} - <StyledDisplayName>{participantName}</StyledDisplayName>
      {` (${numberOfNames} ${memberLabel})`}
    </span>
  );
};

const StyledDisplayName = styled("span")(
  ({ theme }) => css`
    font-weight: 500;
  `
);

export default ParticipantCardDisplayName;
